import type { NodeId } from 'web';
import type { ImageRef } from '@/types/api/assets';
import type { FlowGraph } from '../FlowGraph';

/**
 * Resolves an image suitable for workflow poster (same basis as {@link NodeViewer}): file-backed
 * images use `url` and carry their Blobstore key; computed/canvas previews call WASM
 * `exportNodeRender` (expensive) and have no key until the server uploads them.
 *
 * Intended for on-demand use (e.g. user clicks "Set as cover"). Do not use from render or
 * `useSyncExternalStore` selectors — that would re-run PNG/base64 export every frame.
 */
export function resolveNodePreviewImagePoster(graph: FlowGraph, nodeId: NodeId): ImageRef | undefined {
  if (graph.getNodeType(nodeId) === 'extract_video_frame') {
    const img = graph.wasm.call((wasm) => wasm.exportNodeRender(nodeId, 'result', 2048));
    return img ? { url: img.data } : undefined;
  }

  const evaluated = graph.evaluateNode(nodeId);
  if (evaluated?.type !== 'image') {
    return undefined;
  }

  const val = evaluated.value;
  if (val.type === 'computed') {
    const img = graph.wasm.call((wasm) => wasm.exportNodeRender(nodeId));
    return img ? { url: img.data } : undefined;
  }
  if (val.type === 'file') {
    return { url: val.url, key: val.blobstore?.id };
  }
  return undefined;
}
